import React from 'react';
import { useNavigate } from 'react-router-dom';
import './LegalPages.css';

const PrivacyPolicy: React.FC = () => {
  const navigate = useNavigate();

  return (
    <div className="legal-container">
      <button className="back-button" onClick={() => navigate('/')}>← Back to Home</button>
      
      <h1>Privacy Policy</h1>
      <p className="last-updated">Last Updated: February 1, 2026</p>

      <section>
        <h2>1. Introduction</h2>
        <p>
          Jollyio ("we," "us," or "our") respects your privacy. This Privacy Policy explains how we collect, 
          use, disclose, and protect your information when you use our website and services.
        </p>
      </section>

      <section>
        <h2>2. Information We Collect</h2>
        
        <h3>Information You Provide</h3>
        <ul>
          <li><strong>Account Information:</strong> Email address, name (via Google OAuth)</li>
          <li><strong>Preferences:</strong> Selected city, kids' ages, favorite categories</li>
          <li><strong>Favorites:</strong> Events, camps, venues you bookmark</li>
        </ul>

        <h3>Automatically Collected Information</h3>
        <ul>
          <li><strong>Usage Data:</strong> Pages visited, features used, time spent on site</li>
          <li><strong>Device Information:</strong> Browser type, operating system, IP address</li>
          <li><strong>Location:</strong> Approximate location based on city selection (not GPS)</li>
        </ul>

        <h3>Event Information We Collect</h3>
        <p>
          We collect information about kid-friendly events from publicly available sources including:
        </p>
        <ul>
          <li>Public library event calendars</li>
          <li>Parks and recreation department websites</li>
          <li>School district calendars</li>
          <li>Venue websites (birthday party locations, camps, classes)</li>
          <li>Community organization event pages</li>
        </ul>
      </section>

      <section>
        <h2>3. How We Use Your Information</h2>
        <p>We use collected information to:</p>
        <ul>
          <li>Provide and improve our services</li>
          <li>Personalize event recommendations based on your preferences</li>
          <li>Save your favorite events and preferences</li>
          <li>Calculate distances to events based on your selected city</li>
          <li>Send important service updates (if you opt in)</li>
          <li>Analyze usage patterns to improve the website</li>
          <li>Prevent fraud and ensure website security</li>
        </ul>
      </section>

      <section>
        <h2>4. How We Share Your Information</h2>
        <p>
          <strong>We do not sell your personal information.</strong> We may share information in these limited circumstances:
        </p>
        <ul>
          <li><strong>Service Providers:</strong> Supabase (database), Vercel (hosting), Google (authentication)</li>
          <li><strong>Legal Requirements:</strong> When required by law or to protect our rights</li>
          <li><strong>Business Transfers:</strong> In case of merger, acquisition, or sale of assets</li>
        </ul>
      </section>

      <section>
        <h2>5. Third-Party Links</h2>
        <p>
          Our website links to external event registration pages and venue websites. We are not responsible 
          for their privacy practices. Please review their privacy policies before providing information.
        </p>
      </section>

      <section>
        <h2>6. Cookies and Tracking</h2>
        <p>We use cookies and similar technologies to:</p>
        <ul>
          <li>Remember your city selection and preferences</li>
          <li>Maintain your login session</li>
          <li>Analyze website usage (via analytics tools)</li>
        </ul>
        <p>You can control cookies through your browser settings.</p>
      </section>

      <section>
        <h2>7. Children's Privacy (COPPA Compliance)</h2>
        <p>
          Jollyio is designed to help parents find kid-friendly events. While our content is about children's 
          activities, our service is intended for use by parents and guardians who are 13 years or older.
        </p>
        <ul>
          <li>We do not knowingly collect personal information from children under 13</li>
          <li>Users must be 13+ to create accounts</li>
          <li>If we learn we've collected data from a child under 13, we will delete it promptly</li>
        </ul>
      </section>

      <section>
        <h2>8. Data Security</h2>
        <p>
          We implement reasonable security measures to protect your information, including:
        </p>
        <ul>
          <li>Encrypted connections (HTTPS)</li>
          <li>Secure authentication via Google OAuth</li>
          <li>Regular security updates and monitoring</li>
        </ul>
        <p>
          However, no method of transmission over the internet is 100% secure. We cannot guarantee absolute security.
        </p>
      </section>

      <section>
        <h2>9. Your Rights and Choices</h2>
        <p>You have the right to:</p>
        <ul>
          <li><strong>Access:</strong> Request a copy of your personal data</li>
          <li><strong>Correction:</strong> Update inaccurate information in your account settings</li>
          <li><strong>Deletion:</strong> Request deletion of your account and data</li>
          <li><strong>Opt-Out:</strong> Unsubscribe from marketing emails (if we send them)</li>
          <li><strong>Data Portability:</strong> Request your data in a portable format</li>
        </ul>
        <p>To exercise these rights, contact us at <strong>support@jollyio.com</strong></p>
      </section>

      <section>
        <h2>10. Data Retention</h2>
        <p>
          We retain your information as long as your account is active or as needed to provide services. 
          After account deletion, we may retain certain data for legal compliance (typically 30-90 days).
        </p>
      </section>

      <section>
        <h2>11. California Privacy Rights (CCPA)</h2>
        <p>
          California residents have additional rights under the CCPA. You may request:
        </p>
        <ul>
          <li>Disclosure of personal information collected and shared</li>
          <li>Deletion of personal information</li>
          <li>Opt-out of sale (we do not sell personal information)</li>
        </ul>
        <p>We do not discriminate against users who exercise their privacy rights.</p>
      </section>

      <section>
        <h2>12. Changes to This Policy</h2>
        <p>
          We may update this Privacy Policy periodically. We will notify users of material changes by 
          updating the "Last Updated" date. Continued use after changes constitutes acceptance.
        </p>
      </section>

      <section>
        <h2>13. Contact Us</h2>
        <p>
          For privacy questions or to exercise your rights, contact us at:
        </p>
        <p><strong>Email:</strong> support@jollyio.com</p>
        <p><strong>Address:</strong> Jollyio, Houston, TX 77002</p>
      </section>
    </div>
  );
};

export default PrivacyPolicy;
